import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce, next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, Timeframe } from 'webapp-types';
import { TimeframeType } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';

export enum LinearChangePattern {
	MONTHLY_PERCENT = 'monthly_percent',
	MONTHLY_ABSOLUTE = 'monthly_absolute',
	QUARTERLY_PERCENT = 'quarterly_percent',
	QUARTERLY_ABSOLUTE = 'quarterly_absolute',
	YEARLY_PERCENT = 'yearly_percent',
	YEARLY_ABSOLUTE = 'yearly_absolute',
}

function isPercentagePattern(pattern: LinearChangePattern): boolean {
	return [
		LinearChangePattern.MONTHLY_PERCENT,
		LinearChangePattern.QUARTERLY_PERCENT,
		LinearChangePattern.YEARLY_PERCENT,
	].includes(pattern);
}

const MAX_PERCENTAGE_VALUE = 10000;
const MAX_ABSOLUTE_VALUE = 99999999;

interface Args {
	value: number | null;
	changePattern: LinearChangePattern;
	timeframe: Timeframe | null;
	endDate: string | null;
	onChangeValue: (value: number | null) => void;
	onChangePattern: (changePattern: LinearChangePattern) => void;
	isDisabled?: boolean;
	skipDebounce?: boolean;
}

export default class ForecastsSettingsLinearChangeInputComponent extends Component<Args> {
	@service declare infolayer: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;

	@tracked _value: string | null = this.value !== null ? String(this.value) : null;
	@tracked isPopupOpen = false;
	@tracked updateInputValue = false;

	allOptions: LabelValuePairString[] = [
		{ value: LinearChangePattern.MONTHLY_PERCENT, label: 'Monthly change in %' },
		{ value: LinearChangePattern.MONTHLY_ABSOLUTE, label: 'Monthly change in fixed value' },
		{ value: LinearChangePattern.QUARTERLY_PERCENT, label: 'Quarterly change in %' },
		{ value: LinearChangePattern.QUARTERLY_ABSOLUTE, label: 'Quarterly change in fixed value' },
		{ value: LinearChangePattern.YEARLY_PERCENT, label: 'Yearly change in %' },
		{ value: LinearChangePattern.YEARLY_ABSOLUTE, label: 'Yearly change in fixed value' },
	];

	shortLabels: Record<LinearChangePattern, string> = {
		[LinearChangePattern.MONTHLY_PERCENT]: 'Monthly %',
		[LinearChangePattern.MONTHLY_ABSOLUTE]: 'Monthly fixed',
		[LinearChangePattern.QUARTERLY_PERCENT]: 'Quarterly %',
		[LinearChangePattern.QUARTERLY_ABSOLUTE]: 'Quarterly fixed',
		[LinearChangePattern.YEARLY_PERCENT]: 'Yearly %',
		[LinearChangePattern.YEARLY_ABSOLUTE]: 'Yearly fixed',
	};

	get value() {
		return this.args.value;
	}

	get changePattern() {
		return this.args.changePattern;
	}

	get selectedOption() {
		return (
			this.options.find((option) => option.value === this.changePattern) || this.options[0]
		);
	}

	get dropdownText() {
		return this.shortLabels[this.changePattern];
	}

	get options(): LabelValuePairString[] {
		const timeframe = this.args.timeframe;

		const baseOptions = [
			LinearChangePattern.MONTHLY_PERCENT,
			LinearChangePattern.MONTHLY_ABSOLUTE,
		];

		const conditionalOptions: LinearChangePattern[] = [];

		if (timeframe) {
			const { type } = timeframe;

			if (type === TimeframeType.Y) {
				conditionalOptions.push(
					LinearChangePattern.QUARTERLY_PERCENT,
					LinearChangePattern.QUARTERLY_ABSOLUTE,
					LinearChangePattern.YEARLY_PERCENT,
					LinearChangePattern.YEARLY_ABSOLUTE,
				);
			} else if (
				type === TimeframeType.Q1 ||
				type === TimeframeType.Q2 ||
				type === TimeframeType.Q3 ||
				type === TimeframeType.Q4
			) {
				conditionalOptions.push(
					LinearChangePattern.QUARTERLY_PERCENT,
					LinearChangePattern.QUARTERLY_ABSOLUTE,
				);
			}
		}

		const allowedPatterns = [...new Set([...baseOptions, ...conditionalOptions])];
		return this.allOptions.filter((option) =>
			allowedPatterns.includes(option.value as LinearChangePattern),
		);
	}

	@action
	updateOnOutsideValueChange() {
		this.updateInputValue = true;
		this._value = this.value !== null ? String(this.value) : null;

		next(() => {
			this.updateInputValue = false;
		});
	}

	@action
	updateValue(value: number | null) {
		if (String(value) !== String(this.value)) {
			// validation
			if (value !== null) {
				const absValue = Math.abs(value);
				const isPercentage = isPercentagePattern(this.changePattern);
				const maxValue = isPercentage ? MAX_PERCENTAGE_VALUE : MAX_ABSOLUTE_VALUE;

				if (absValue > maxValue) {
					const formattedMaxValue = maxValue.toLocaleString();
					const message = isPercentage
						? `Percentage value cannot exceed ${formattedMaxValue}%`
						: `Absolute value cannot exceed ${formattedMaxValue}`;
					this.infolayer.warning(message);
					return;
				}
			}

			this._value = String(value);
			if (this.args.skipDebounce) {
				this.args.onChangeValue(value);
			} else {
				debounce(this, this.debouncedValueUpdate, value, 400);
			}
		}
	}

	@action
	openDropdown(event: PointerEvent): void {
		if (this.isPopupOpen) {
			return;
		}

		const dropDownEl = (event.target as HTMLElement).closest('.dropdown')!,
			attachTo = dropDownEl.querySelector<HTMLElement>('ui-icon')!;

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createListPopup(
			attachTo,
			'',
			'',
			true, // allowStacked = true
		);

		if (popup) {
			const listItems = this.options.map((option) => ({
				action: option.value,
				text: option.label,
				isSelected: option.value === this.changePattern,
			}));

			popup.open({
				itemsList: listItems,
				doAction: (actionName: string) => {
					if (actionName) {
						this.onChange(actionName as LinearChangePattern);
					}
				},
			});
		}
	}

	@action
	onChange(changePattern: LinearChangePattern) {
		this.args.onChangePattern(changePattern);
		// Clear the input value when pattern changes
		this._value = null;
		this.args.onChangeValue(null);
	}

	private debouncedValueUpdate(value: number | null) {
		this.args.onChangeValue(value);
	}
	<template>
		<div
			class="input-dropdown linear-change-input"
			{{didUpdate this.updateOnOutsideValueChange @value}}
		>
			<FormInputDecimalComponent
				@value={{this._value}}
				@onChange={{this.updateValue}}
				@placeholder="Value"
				@isDisabled={{@isDisabled}}
				@forceUpdateFromValue={{this.updateInputValue}}
			/>

			<div class="dropdown">
				<a role="button" {{on "click" this.openDropdown}}>
					<div>
						{{this.dropdownText}}
					</div>
					<CommonUiIconComponent @name="ChevronDown" @size="xs" />
				</a>
			</div>
		</div>
	</template>
}
